// src/lib/employeeApi.js
import api from "./api";

/**
 * Fetches a paginated and filtered list of employees.
 * @param {object} filters - An object containing filters like page, limit, department, etc.
 * @returns {Promise<Object>} The API response with employees, total, etc.
 */
export const getEmployees = async (filters = {}) => {
  const response = await api.get('/employees', {
    params: filters, // Pass the filters object directly as query parameters
  });
  return response.data.data;
};

/**
 * Fetches a single employee by their string ID (e.g., 'EMP001').
 * @param {string} employeeId - The employee's ID.
 * @returns {Promise<Object>} The employee data.
 */
export const getEmployeeById = async (employeeId) => {
  const { data } = await api.get(`/employees/${employeeId}`);
  return data;
};

/**
 * Searches for employees by name.
 * @param {string} name - The search term.
 * @returns {Promise<Array>} A list of matching employees.
 */
export const searchEmployees = async (name) => {
  const { data } = await api.get("/employees/search", {
    params: { name },
  });
  return data;
};

/**
 * Creates a new employee. Handles file uploads.
 * @param {Object} employeeData - The employee form data.
 * @returns {Promise<Object>} The newly created employee data.
 */
export const createEmployee = async (employeeData) => {
  const formData = new FormData();

  Object.keys(employeeData).forEach((key) => {
    if (employeeData[key]) {
      formData.append(key, employeeData[key]);
    }
  });
  const { data } = await api.post("/employees", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/**
 * Updates an existing employee.
 * @param {string} employeeId - The ID of the employee to update.
 * @param {Object} updateData - The data to update.
 * @returns {Promise<Object>} The updated employee data.
 */
// api/employees.js
export const updateEmployee = async (employeeId, updateData) => {
  // Clean out undefined or null values
  const filteredData = Object.fromEntries(
    Object.entries(updateData).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );

  // Send as JSON instead of FormData
  const { data } = await api.patch(`/employees/${employeeId}`, filteredData, {
    headers: { "Content-Type": "application/json" },
  });

  return data;
};



/**
 * Soft deletes an employee (sets their status to 'inactive').
 * @param {string} employeeId - The ID of the employee to delete.
 * @returns {Promise<Object>} The API response message.
 */
export const deleteEmployee = async (employeeId) => {
  const { data } = await api.delete(`/employees/${employeeId}`);
  return data;
};
